import { useCallback, useEffect, useRef, useState } from 'react';
import type { Issue } from '../types';
import { useApp } from '../store';
import { displayName } from '../people';
import { currentWorkspace } from '../workspace';
import { composePortfolioPack } from '../portfolio-pack';
import { portfolioPackPdf } from '../portfolio-pack-pdf';
import Expander from './Expander';
import Icon from './Icon';
import RollupTable from './portfolio/RollupTable';
import PortfolioMixBand from './portfolio/PortfolioMixBand';
import MixBar from './portfolio/MixBar';
import ByPerson from './portfolio/ByPerson';
import MilestonesLens from './portfolio/MilestonesLens';
import EpicsLens, { boardTiersFromRows } from './portfolio/EpicsLens';
import AreasLens from './portfolio/AreasLens';
import ProjectStatus from './portfolio/ProjectStatus';
import BlockedCards from './portfolio/BlockedCards';
import LeanCardDetail from './portfolio/LeanCardDetail';
import EpicDetail from './portfolio/EpicDetail';
import AreaDetail from './portfolio/AreaDetail';

/**
 * THE PORTFOLIO — every project on the tenant, one row each (#385).
 *
 * This is the SHELL: it owns the data (loads the roll-up on open, from the
 * store's `loadPortfolio`) and the drill state — the list, one project's status
 * report, and the blocked cards behind that report — and renders one of the
 * three screens accordingly. Each screen is a leaf that draws from its props and
 * nothing else, so the three can be built and read on their own.
 *
 * The drill is in-page state, not a route: Portfolio → a project's report → its
 * blocked cards, with a breadcrumb back. Opening the real board is the one move
 * that leaves the portfolio — it switches the active project and lands on the
 * Board, which is what "Open the board" says and where reviewing that project's
 * work actually begins.
 */
type Drill =
  | { level: 'list' }
  | { level: 'report'; id: string }
  | { level: 'cards'; id: string }
  | { level: 'epic'; name: string }
  | { level: 'area'; name: string };

export default function Portfolio() {
  const rows = useApp((s) => s.portfolio);
  const loading = useApp((s) => s.portfolioLoading);
  const error = useApp((s) => s.portfolioError);
  const at = useApp((s) => s.portfolioAt);
  const accountability = useApp((s) => s.accountability);
  const cardsByBoard = useApp((s) => s.portfolioIssues);
  const workCalendar = useApp((s) => s.workCalendar);
  const areasByBoard = useApp((s) => s.portfolioAreas);
  const pulsesByBoard = useApp((s) => s.portfolioPulses);
  const phasesByBoard = useApp((s) => s.portfolioPhases);
  const milestonesByBoard = useApp((s) => s.portfolioMilestones);
  const milestonePulsesByBoard = useApp((s) => s.milestonePulses);
  const portfolioClosures = useApp((s) => s.portfolioClosures);
  const portfolioLessons = useApp((s) => s.portfolioLessons);
  const profiles = useApp((s) => s.profiles);
  const user = useApp((s) => s.user);
  const currency = useApp((s) => s.currency);
  const projectCount = useApp((s) => s.projects.length);
  const loadPortfolio = useApp((s) => s.loadPortfolio);
  const setProject = useApp((s) => s.setProject);
  const setView = useApp((s) => s.setView);
  const setHandoffRequest = useApp((s) => s.setHandoffRequest);
  const issueTypes = useApp((s) => s.issueTypes);
  const openIssue = useApp((s) => s.openIssue);
  const setPortfolioReturn = useApp((s) => s.setPortfolioReturn);
  // The person's saved Portfolio views + the one they open on, and the actions
  // that write them (through the profile, so they follow between machines).
  const savedViews = useApp((s) => s.portfolioViews);
  const defaultViewId = useApp((s) => s.portfolioDefault);
  const savePortfolioView = useApp((s) => s.savePortfolioView);
  const updatePortfolioView = useApp((s) => s.updatePortfolioView);
  const renamePortfolioView = useApp((s) => s.renamePortfolioView);
  const forgetPortfolioView = useApp((s) => s.forgetPortfolioView);
  const setPortfolioDefault = useApp((s) => s.setPortfolioDefault);

  // Start on the drill we left, if we're coming back from a card opened here —
  // openFull stashed it so the full card's "← Portfolio" lands on the same spot,
  // not the top of the list. Read once (getState, no subscription); cleared just
  // below so it is a one-shot restore.
  const [drill, setDrill] = useState<Drill>(() => useApp.getState().portfolioReturn ?? { level: 'list' });
  // The card opened in place — the lean read-only panel over the list. Held by
  // the shell (not either lens) because BOTH lenses open it, and it resolves the
  // full card from the roll-up's already-loaded cards, so opening costs no read.
  const [card, setCard] = useState<{ issue: Issue; projectId: string; context: string } | null>(null);
  // The lean card opens as the app's right slide-over (uniform with My Work and
  // the budget ledger — Vinnie, 6 Oct), so closing runs the drawer's slide-out
  // before the card unmounts. Same little state machine My Work uses: mark it
  // leaving, then clear after the animation (reduced motion closes at once).
  const [cardLeaving, setCardLeaving] = useState(false);
  const cardTimer = useRef<ReturnType<typeof setTimeout>>();
  const PANEL_MS = 260;
  // Whether the "boards not yet run as projects" list is expanded (collapsed by
  // default — they are offered, never in the way).
  const [showOtherBoards, setShowOtherBoards] = useState(false);
  // Which lens the list is read through — by project (the roll-up) or by person
  // (the accountability view). A list-level choice only; the drills are unaffected.
  // Remembered per viewer, so someone who works in the People view is not put back
  // on Projects every time they open the Portfolio (localStorage can throw in a
  // private window — fall back to Projects and never let it break the screen).
  const [lens, setLensState] = useState<'projects' | 'people' | 'milestones' | 'epics' | 'areas'>(() => {
    try {
      const v = localStorage.getItem('fd.portfolio.lens');
      return v === 'people' || v === 'milestones' || v === 'epics' || v === 'areas' ? v : 'projects';
    } catch {
      return 'projects';
    }
  });
  const setLens = (v: 'projects' | 'people' | 'milestones' | 'epics' | 'areas') => {
    setLensState(v);
    try {
      localStorage.setItem('fd.portfolio.lens', v);
    } catch {
      /* a private window with storage blocked — the choice just won't persist */
    }
  };

  // Read the roll-up on open, and again when the set of projects changes (a
  // board arrives, is added, or is archived) so a late-loading list is not left
  // out of the pulse.
  useEffect(() => {
    void loadPortfolio();
  }, [loadPortfolio, projectCount]);

  // The drill above consumed any stashed return on mount; clear it now so a card
  // opened later by some other route never wears the "← Portfolio" breadcrumb.
  useEffect(() => {
    if (useApp.getState().portfolioReturn) setPortfolioReturn(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A DRILL SWAPS THE CONTENT BUT KEEPS THE SCROLL CONTAINER — so reset it.
  //
  // Opening a project's report, its blocked cards, or an epic/area is in-page
  // state (`drill`), not an app view change, so the one `.view` scroll container
  // stays mounted and holds the position it had in the list. Clicking a project
  // LOW in a scrolled list therefore opened its report already scrolled past its
  // own top — you had to scroll up to read it (Vinnie, 21 Oct). On entering a
  // drill, send the container to the top so each sub-screen starts at its heading;
  // on the way back to the list, put back the place we left, so stepping into a
  // project and out again does not lose your spot. `.view` is the app's scroll
  // container (App shell), and there is exactly one while the Portfolio is up.
  const prevDrillLevel = useRef(drill.level);
  const listScrollTop = useRef(0);
  useEffect(() => {
    const el = document.querySelector('.view');
    if (el) {
      const was = prevDrillLevel.current;
      const now = drill.level;
      if (was === 'list' && now !== 'list') {
        listScrollTop.current = el.scrollTop; // remember our place in the list
        el.scrollTo({ top: 0 }); // start the drill at the top
      } else if (was !== 'list' && now === 'list') {
        el.scrollTo({ top: listScrollTop.current }); // restore it on the way back
      } else if (was !== now) {
        el.scrollTo({ top: 0 }); // between drills (report ↔ cards) — top again
      }
    }
    prevDrillLevel.current = drill.level;
  }, [drill]);

  const openBoard = (id: string) => {
    setProject(id);
    setView('board');
  };

  // PRODUCE THE MONTHLY STEERING PACK — the whole portfolio as a branded A4 PDF,
  // composed from the rows already loaded (no read) and downloaded straight to the
  // browser. v1 is produce-and-download only: a portfolio snapshot is not a
  // signable document, so nothing is stored and no document row is minted. Gated by
  // being on the Portfolio at all — anyone who can see it can produce it.
  const [producingPack, setProducingPack] = useState(false);
  const producePack = async () => {
    if (producingPack || rows.length === 0) return;
    setProducingPack(true);
    try {
      const workspaceName = currentWorkspace();
      const model = composePortfolioPack(
        rows,
        { workspaceName, asOf: at ?? Date.now() },
        pulsesByBoard,
        {
          phasesByBoard,
          standaloneByBoard: milestonesByBoard,
          pulsesByBoard: milestonePulsesByBoard,
          // The cards per board, so the pack's milestone table carries the same
          // cadence forecast the screen shows (0215).
          issuesByBoard: cardsByBoard,
          // The workspace working calendar (0216), so the pack's predicted dates
          // skip known future non-working days just as the screen does.
          calendar: workCalendar,
        },
        // LEARNING & CLOSURE (#491) — the signed after-action reports and this
        // cycle's recommendation-bearing lessons, lifted to plain inputs at load.
        { closures: portfolioClosures, lessons: portfolioLessons },
      );
      const bytes = await portfolioPackPdf(model, {
        // Stamped once here, so the same portfolio twice is the same file.
        preparedOn: Date.now(),
        preparedBy: user?.email ? displayName(user.email, profiles) : '',
        workspaceName,
      });
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Monthly Steering Pack — ${model.period}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      /* Producing the pack should never break the screen — the portfolio is still
         fully usable, and the person can try again. */
    }
    setProducingPack(false);
  };

  // Open a card's lean detail in place — resolve the full card from the cards the
  // roll-up already loaded (no read), and name the board it sits on for the header.
  const openCard = (projectId: string, cardId: string) => {
    // Match on the card id OR its display ref (e.g. "NET-123"): the portfolio drill's
    // lists (waiting, slipping, work-in-flight) carry the ref a reader sees, not the id,
    // so every "NET-123" on the report can be clicked straight to the card.
    const issue = (cardsByBoard.get(projectId) ?? []).find((i) => i.id === cardId || i.ref === cardId);
    if (!issue) return;
    const row = rows.find((r) => r.id === projectId);
    const name = row?.name ?? 'Board';
    const kind = row && row.phases.length ? 'Project' : 'Board';
    clearTimeout(cardTimer.current);
    cardTimer.current = undefined;
    setCardLeaving(false);
    setCard({ issue, projectId, context: `${name} · ${kind}` });
  };

  // Close with the slide-out, then clear once it has left (reduced motion closes
  // at once). Mirrors My Work's / the budget drawer's state machine.
  const closeCard = useCallback(() => {
    const quick = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (quick) {
      setCard(null);
      return;
    }
    if (cardTimer.current) return;
    setCardLeaving(true);
    cardTimer.current = setTimeout(() => {
      cardTimer.current = undefined;
      setCardLeaving(false);
      setCard(null);
    }, PANEL_MS);
  }, []);
  useEffect(() => () => clearTimeout(cardTimer.current), []);

  // "Open the full card" — the one move that leaves the portfolio: switch to the
  // card's board and open it there, then close the panel. We're navigating away,
  // so drop it at once (no slide-out) and cancel any pending close.
  const openFull = () => {
    if (!card) return;
    clearTimeout(cardTimer.current);
    cardTimer.current = undefined;
    setCardLeaving(false);
    openBoard(card.projectId);
    openIssue(card.issue.id);
    // Arm the way back AFTER openIssue (which clears any stale return): the full
    // card shows a "← Portfolio" that lands on the exact drill we're leaving.
    setPortfolioReturn(drill);
    setCard(null);
  };

  // A steering roll-up lists PROJECTS — boards run as one (a plan, phases, a budget,
  // or on hold) — AND live services (a board that has been handed over or is run as
  // continuous is in service even without a project plan, #435 surface 2). A bare
  // development/kanban board tracks work but is neither, so an empty one no longer
  // reads "On track" in the lenses; the excluded boards are offered below the list so
  // they are never hidden, only kept out of the counts.
  const projectRows = rows.filter((r) => r.tracked || r.tier === 'inservice');
  const otherBoards = rows.filter((r) => !r.tracked && r.tier !== 'inservice');

  // The drilled-into row, if it is still in the (possibly just-reloaded) list.
  const drillRow =
    drill.level === 'report' || drill.level === 'cards'
      ? rows.find((r) => r.id === drill.id)
      : undefined;

  // The By-epic drill — an epic across every board it touches. It reads the same
  // cross-board cards the lens does and opens a card in the shell's lean panel, so
  // the drill never leaves the Portfolio.
  if (drill.level === 'epic') {
    return (
      <>
        <EpicDetail
          key={drill.name}
          name={drill.name}
          cardsByBoard={cardsByBoard}
          boardTiers={boardTiersFromRows(rows)}
          profiles={profiles}
          onBack={() => setDrill({ level: 'list' })}
          onOpenCard={openCard}
        />
        {card && (
          <div className="overlay pf-scene" onClick={closeCard}>
            <LeanCardDetail
              issue={card.issue}
              context={card.context}
              issueTypes={issueTypes}
              profiles={profiles}
              resolveName={(who) => displayName(who, profiles)}
              now={at ?? Date.now()}
              leaving={cardLeaving}
              onClose={closeCard}
              onOpenFull={openFull}
            />
          </div>
        )}
      </>
    );
  }

  // The By-area drill — an area across every board it touches, the permanent-
  // taxonomy twin of the epic drill. Reads the same cross-board cards (resolved to
  // an area name through each board's own area list) and opens a card in the
  // shell's lean panel, so the drill never leaves the Portfolio.
  if (drill.level === 'area') {
    return (
      <>
        <AreaDetail
          key={drill.name}
          name={drill.name}
          cardsByBoard={cardsByBoard}
          areasByBoard={areasByBoard}
          boards={rows}
          profiles={profiles}
          onBack={() => setDrill({ level: 'list' })}
          onOpenCard={openCard}
        />
        {card && (
          <div className="overlay pf-scene" onClick={closeCard}>
            <LeanCardDetail
              issue={card.issue}
              context={card.context}
              issueTypes={issueTypes}
              profiles={profiles}
              resolveName={(who) => displayName(who, profiles)}
              now={at ?? Date.now()}
              leaving={cardLeaving}
              onClose={closeCard}
              onOpenFull={openFull}
            />
          </div>
        )}
      </>
    );
  }

  if (drill.level === 'report' && drillRow) {
    return (
      <>
        <ProjectStatus
          row={drillRow}
          pulses={pulsesByBoard.get(drillRow.id) ?? []}
          phases={phasesByBoard.get(drillRow.id) ?? []}
          milestones={milestonesByBoard.get(drillRow.id) ?? []}
          milestonePulses={milestonePulsesByBoard.get(drillRow.id) ?? []}
          cards={cardsByBoard.get(drillRow.id) ?? []}
          calendar={workCalendar}
          currency={currency}
          onBack={() => setDrill({ level: 'list' })}
          onOpenBoard={openBoard}
          onOpenBlockers={(id) => setDrill({ level: 'cards', id })}
          onOpenCard={openCard}
        />
        {/* A card drilled from the report (a waiting / slipping / work-in-flight ref)
            opens in the same lean panel the epic and area drills use, so the report
            never leaves the Portfolio. */}
        {card && (
          <div className="overlay pf-scene" onClick={closeCard}>
            <LeanCardDetail
              issue={card.issue}
              context={card.context}
              issueTypes={issueTypes}
              profiles={profiles}
              resolveName={(who) => displayName(who, profiles)}
              now={at ?? Date.now()}
              leaving={cardLeaving}
              onClose={closeCard}
              onOpenFull={openFull}
            />
          </div>
        )}
      </>
    );
  }
  if (drill.level === 'cards' && drillRow) {
    return (
      <BlockedCards
        row={drillRow}
        onBack={() => setDrill({ level: 'report', id: drillRow.id })}
        onOpenBoard={openBoard}
        onOpenCard={openCard}
      />
    );
  }

  // The list — also the fallback if a drilled-into project has left the roll-up.
  // A lens toggle sits above it: the roll-up (by project) or the accountability
  // view (by person), over the same tenant read. The toggle is list-level only —
  // the two drills below never show it.
  return (
    <>
      <div className="pf-lens" role="tablist" aria-label="Portfolio lens">
        <button
          type="button"
          className={`seg-btn${lens === 'projects' ? ' active' : ''}`}
          role="tab"
          aria-selected={lens === 'projects'}
          onClick={() => setLens('projects')}
        >
          Projects
        </button>
        <button
          type="button"
          className={`seg-btn${lens === 'people' ? ' active' : ''}`}
          role="tab"
          aria-selected={lens === 'people'}
          onClick={() => setLens('people')}
        >
          People
        </button>
        <button
          type="button"
          className={`seg-btn${lens === 'milestones' ? ' active' : ''}`}
          role="tab"
          aria-selected={lens === 'milestones'}
          onClick={() => setLens('milestones')}
        >
          Milestones
        </button>
        <button
          type="button"
          className={`seg-btn${lens === 'epics' ? ' active' : ''}`}
          role="tab"
          aria-selected={lens === 'epics'}
          onClick={() => setLens('epics')}
        >
          Epics
        </button>
        <button
          type="button"
          className={`seg-btn${lens === 'areas' ? ' active' : ''}`}
          role="tab"
          aria-selected={lens === 'areas'}
          onClick={() => setLens('areas')}
        >
          Areas
        </button>
      </div>

      {/* THE PORTFOLIO HEADER PANEL (#435, Option B) — one banded header that owns
          the roll-up's title + subtitle, with the lifecycle counts as the hero and
          the Run-vs-Change split on a quiet bottom strip. Projects lens only (the
          roll-up it heads). It renders in every state — loading (holds its frame +
          title so nothing shifts) and error (keeps the title above the message) —
          so the header is always present. */}
      {lens === 'projects' && <PortfolioMixBand rows={rows} loading={loading} at={at} />}

      {lens === 'epics' ? (
        <EpicsLens
          rows={rows}
          cardsByBoard={cardsByBoard}
          profiles={profiles}
          loading={loading}
          error={error}
          onRetry={() => void loadPortfolio()}
          onOpenEpic={(name) => setDrill({ level: 'epic', name })}
        />
      ) : lens === 'areas' ? (
        <AreasLens
          rows={rows}
          cardsByBoard={cardsByBoard}
          areasByBoard={areasByBoard}
          profiles={profiles}
          loading={loading}
          error={error}
          onRetry={() => void loadPortfolio()}
          onOpenArea={(name) => setDrill({ level: 'area', name })}
        />
      ) : lens === 'milestones' ? (
        <MilestonesLens
          rows={projectRows}
          loading={loading}
          error={error}
          at={at}
          phasesByBoard={phasesByBoard}
          milestonesByBoard={milestonesByBoard}
          issuesByBoard={cardsByBoard}
          calendar={workCalendar}
          onOpen={(id) => setDrill({ level: 'report', id })}
          onRetry={() => void loadPortfolio()}
        />
      ) : lens === 'people' ? (
        error ? (
          // The same read feeds both lenses, so a failed load is a failed load
          // here too — say so and offer the retry rather than an empty "no work".
          <div className="pf-board" style={{ padding: '16px 14px' }}>
            <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 12 }}>
              The portfolio could not be read. {error}
            </div>
            <button className="btn" onClick={() => void loadPortfolio()}>Try again</button>
          </div>
        ) : (
          <ByPerson acc={accountability} loading={loading} at={at} calendar={workCalendar} onOpenBoard={openBoard} onOpenCard={openCard} />
        )
      ) : (
        <RollupTable
          rows={projectRows}
          loading={loading}
          error={error}
          at={at}
          currency={currency}
          onOpen={(id) => setDrill({ level: 'report', id })}
          onRetry={() => void loadPortfolio()}
          cardsByBoard={cardsByBoard}
          pulsesByBoard={pulsesByBoard}
          phasesByBoard={phasesByBoard}
          milestonesByBoard={milestonesByBoard}
          calendar={workCalendar}
          resolveName={(who) => displayName(who, profiles)}
          onOpenCard={openCard}
          onMarkLive={(id) => { setHandoffRequest(id); setProject(id); setView('board'); }}
          onProducePack={() => void producePack()}
          producingPack={producingPack}
          savedViews={savedViews}
          defaultViewId={defaultViewId}
          onSaveView={savePortfolioView}
          onUpdateView={updatePortfolioView}
          onRenameView={renamePortfolioView}
          onForgetView={forgetPortfolioView}
          onSetDefault={setPortfolioDefault}
        />
      )}

      {/* Boards that are not run as projects — kept out of the health counts and
          the pack, but offered here so nothing is hidden. A board becomes a project
          the moment it is given a plan, phases, or a budget. */}
      {lens !== 'people' && lens !== 'epics' && !loading && !error && otherBoards.length > 0 && (
        <div className="pf-otherboards">
          <button
            type="button"
            className={`pf-ob-toggle${showOtherBoards ? ' is-open' : ''}`}
            aria-expanded={showOtherBoards}
            onClick={() => setShowOtherBoards((v) => !v)}
          >
            <span className="pf-ob-chev" aria-hidden="true"><Icon name="chevron" size={18} /></span>
            {otherBoards.length} {otherBoards.length === 1 ? 'board is' : 'boards are'} not yet set up as{' '}
            {otherBoards.length === 1 ? 'a project' : 'projects'}
          </button>
          <Expander open={showOtherBoards}>
            <p className="pf-ob-note">
              These track work but have no plan, phases or budget, so they are not in the roll-up
              or the pack. Open one to set it up as a project.
            </p>
            <ul className="pf-ob-list">
              {otherBoards.map((b) => (
                <li key={b.id}>
                  <button type="button" className="pf-ob-item" onClick={() => openBoard(b.id)}>
                    <span className="pf-ob-name">{b.name}</span>
                    <span className="pf-ob-owner">{b.owner}</span>
                    {/* The Run-vs-Change mix shows here too (#435), so the most
                        run-heavy boards — often the ones not run as projects —
                        are still comparable at a glance. */}
                    <span className="pf-ob-mix"><MixBar mix={b.mix} /></span>
                  </button>
                </li>
              ))}
            </ul>
          </Expander>
        </div>
      )}

      {card && (
        <div className="overlay pf-scene" onClick={closeCard}>
          <LeanCardDetail
            issue={card.issue}
            context={card.context}
            issueTypes={issueTypes}
            profiles={profiles}
            resolveName={(who) => displayName(who, profiles)}
            now={at ?? Date.now()}
            leaving={cardLeaving}
            onClose={closeCard}
            onOpenFull={openFull}
          />
        </div>
      )}
    </>
  );
}
